/**
 * Spies (owner, 2026-10-29: "replace TornStats … everything it does": its spy log, "store spy", spy on a user / a faction).
 *
 * How spies work in Torn: a spy is a company job special (Torn's companies.php — e.g. Law Firm and Television Network
 * specials; the Army city job on jobs.php had one too). The player spends job points, picks a target and Torn shows the
 * result on that page, in the special's confirmation box (`.specials-confirm-cont ul.job-info`): "Strength: 1,234,567",
 * "Speed: …", "Dexterity: …", "Defense: …", "Total: …" — any one may be "N/A" (the spy didn't find it). Torn's page gets it
 * as JSON (`result.msg` { strength, speed, dexterity, defense|defence, total } as comma strings, `result.user`
 * { userID, playername, level }). No event, message or API selection carries it: the page is the only place. Other tools
 * copy it as text ("Name: …", "Level: …", "You managed to get the following results:", then the five lines) or keep it
 * (TornStats' "store spy"). So a spy here comes from (a) the player pasting that text (or Torn's JSON, or a TornStats-style
 * row) on the website or the extension's own page, or (b) the extension reading Torn's answer on the job special's page
 * while that page is being viewed (core/page-data.ts gate).
 *
 * What's kept (apps/api/src/spies.ts): the spy for the player who recorded it and their faction; with "Share my spies
 * with every Omni-Torn user" on (from SPY_SHARE_TERMS), for every Omni-Torn user too. Its total, rounded to 2 significant
 * figures, also goes to the player database as an estimate with method 'spy' (the most trusted — player-db.ts
 * ESTIMATE_METHOD_TRUST). New sharing: only from terms version SPY_TERMS (player-db.ts).
 */

import { ESTIMATE_METHOD_TRUST, roundSignificant, SPY_TERMS, trustOf, type EstimateMethod } from './player-db';
import type { WebPrefs } from './web';

export { SPY_TERMS };

/**
 * "Share my spies with every Omni-Torn user" (owner, 2026-10-31: "I'd actually also like an opt in share spy data with all
 * omnitorn users option. Once shared it gets recorded and doesnt delete with your data"): off unless switched on (WebPrefs
 * `shareSpies`, synced like the other preferences), and only for players who agreed to this terms version (the table names
 * it). Spies recorded while it's on are also kept for every invited Omni-Torn user — the four stats, total, the target's
 * name and level, when; never who recorded them — and stay when the recorder deletes their data or switches it off
 * (switching off only stops later spies being shared).
 */
export const SPY_SHARE_TERMS = 12;

/** Are this player's new spies shared with every Omni-Torn user (switched on, and agreed to SPY_SHARE_TERMS)? */
export const spiesForEveryone = (p: Pick<WebPrefs, 'shareSpies' | 'terms'>): boolean => p.shareSpies === true && (p.terms ?? 0) >= SPY_SHARE_TERMS;

/** The switch's words (website Spies page and Account, the extension's options page). */
export const SPY_SHARE_LABEL = 'Share my spies with every Omni-Torn user';
export const SPY_SHARE_HELP =
  'Off unless you switch it on. Spies you record while it’s on are also kept for every Omni-Torn user — the four stats, total, the player’s name and level, and when; never who recorded them. Once shared a spy stays shared (the newest 20 per player), even if you switch this off (that only stops later spies being shared) or delete your data. Removing a spy you recorded takes it out everywhere.';

export const SPY_STATS = ['strength', 'defense', 'speed', 'dexterity', 'total'] as const;
export type SpyStat = (typeof SPY_STATS)[number];
const PARTS = ['strength', 'defense', 'speed', 'dexterity'] as const;

/** One spy: a player's battle stats as Torn's spy found them, at a time. A stat the spy didn't find is null. */
export interface Spy {
  /** The player spied on. */
  target: number;
  name?: string;
  level?: number;
  strength: number | null;
  defense: number | null;
  speed: number | null;
  dexterity: number | null;
  total: number;
  /** When the spy was made (ms). */
  at: number;
}

/** What a paste gave: a spy, or one with no target yet (the page asks which player it was). */
export type ParsedSpy = Omit<Spy, 'target'> & {
  target: number | null;
  /** The stat worked out from the others (Torn said "N/A" for it), if any. */
  derived?: SpyStat;
  /** The numbers were shortened ("1.61b"): close, not exact. */
  approx?: boolean;
};

/** POST /api/spies — at most this many per call. */
export const SPY_MAX_BATCH = 50;
/** A spy older than this isn't taken. */
export const SPY_MAX_AGE_DAYS = 400;
/** Per target, the newest this many are kept for each faction (and each factionless player). */
export const SPY_KEEP_PER_TARGET = 20;
/** GET /api/spies?ids= — at most this many players. */
export const SPY_MAX_IDS = 100;
/** The spy log's page size. */
export const SPY_LOG_PAGE = 50;
/** Battle stats can't be this big. */
const STAT_MAX = 1e16;
/** The four stats must add up to the total within this share (shortened numbers round). */
const SUM_TOLERANCE = 0.02;

/** One spy as the server answers it: whether you recorded it (else: a faction mate or an Omni-Torn user did — never who). */
export interface SpyEntry extends Spy {
  mine: boolean;
  /** Shared with every Omni-Torn user (its recorder's "Share my spies" was on). */
  shared?: true;
  /** Not yours or your faction's: you see it because it was shared with every Omni-Torn user ("an Omni-Torn user's"). */
  omni?: true;
}

/** Who a spy is from, in words: "you", "a faction mate", "an Omni-Torn user". */
export const spyFrom = (s: Pick<SpyEntry, 'mine' | 'omni'>): 'you' | 'a faction mate' | 'an Omni-Torn user' => (s.mine ? 'you' : s.omni ? 'an Omni-Torn user' : 'a faction mate');
/** Whose spy, in words: "yours", "a faction mate’s", "an Omni-Torn user’s". */
export const spyWhose = (s: Pick<SpyEntry, 'mine' | 'omni'>): string => (s.mine ? 'yours' : `${spyFrom(s)}’s`);

/** POST /api/spies answer. */
export interface SpyRecordAnswer {
  ok: boolean;
  kept: number;
  skipped: number;
  /** How many were also shared with every Omni-Torn user (their "Share my spies" is on). */
  shared?: number;
  message?: string;
}

/** GET /api/spies?ids= answer: per player, newest first (yours, your faction's and those shared with every Omni-Torn user). */
export interface SpiesAnswer {
  spies: Record<string, SpyEntry[]>;
}

/** GET /api/spies/log answer: the newest spies you and your faction recorded. */
export interface SpyLogAnswer {
  spies: SpyEntry[];
  offset: number;
  more: boolean;
}

// ---------------------------------------------------------------- checking

const fin = (x: unknown, lo: number, hi: number): number | null => (typeof x === 'number' && Number.isFinite(x) && x >= lo && x <= hi ? x : null);

/** A spy as it may be kept, or null (bad target or time, no total, the four don't add up to the total). */
export function cleanSpy(x: unknown, now: number): Spy | null {
  if (!x || typeof x !== 'object' || Array.isArray(x)) return null;
  const o = x as Record<string, unknown>;
  const target = o.target;
  if (!Number.isInteger(target) || (target as number) <= 0 || (target as number) > 1e8) return null;
  const at = fin(o.at, 0, now + 60_000);
  if (at === null || at < now - SPY_MAX_AGE_DAYS * 86_400_000) return null;
  const stat = (k: SpyStat): number | null | undefined => {
    const v = o[k];
    if (v === null || v === undefined) return null;
    const n = fin(v, 0, STAT_MAX);
    return n === null ? undefined : Math.round(n);
  };
  const s = { strength: stat('strength'), defense: stat('defense'), speed: stat('speed'), dexterity: stat('dexterity'), total: stat('total') };
  if (Object.values(s).some((v) => v === undefined)) return null;
  const done = complete(s as Record<SpyStat, number | null>);
  if (!done.total || done.total <= 0) return null;
  if (!addsUp(done)) return null;
  const out: Spy = { target: target as number, strength: done.strength, defense: done.defense, speed: done.speed, dexterity: done.dexterity, total: done.total, at: Math.min(Math.round(at), now) };
  if (typeof o.name === 'string' && /^[\w-]{1,30}$/.test(o.name.trim())) out.name = o.name.trim();
  const level = o.level;
  if (Number.isInteger(level) && (level as number) >= 1 && (level as number) <= 100) out.level = level as number;
  return out;
}

/** The four stats (where all are known) add up to the total. */
function addsUp(s: Record<SpyStat, number | null>): boolean {
  if (PARTS.some((k) => s[k] === null) || !s.total) return true;
  const sum = PARTS.reduce((a, k) => a + (s[k] ?? 0), 0);
  return Math.abs(sum - s.total) <= s.total * SUM_TOLERANCE + 4;
}

/** The one missing stat worked out from the others (the four add up to the total). */
function complete(s: Record<SpyStat, number | null>): Record<SpyStat, number | null> & { derived?: SpyStat } {
  const missing = SPY_STATS.filter((k) => s[k] === null);
  if (missing.length !== 1) return { ...s };
  const k = missing[0]!;
  const parts = PARTS.filter((p) => p !== k).reduce((a, p) => a + (s[p] ?? 0), 0);
  const v = k === 'total' ? parts : (s.total ?? 0) - parts;
  return v >= 0 ? { ...s, [k]: v, derived: k } : { ...s };
}

/** The total that goes to the player database as an estimate (everyone sees estimates; the spy itself stays with the faction). */
export const spyEstimateTotal = (s: Pick<Spy, 'total'>): number => roundSignificant(s.total, 2);

// ---------------------------------------------------------------- reading pasted text

const STAT_WORDS: Array<[RegExp, SpyStat]> = [
  [/^(?:strength|str)$/i, 'strength'],
  [/^(?:defen[cs]e|def)$/i, 'defense'],
  [/^(?:speed|spd|spe)$/i, 'speed'],
  [/^(?:dexterity|dex)$/i, 'dexterity'],
  [/^(?:total|tot|battle\s*stats?|bs)$/i, 'total'],
];
const statOf = (word: string): SpyStat | null => STAT_WORDS.find(([re]) => re.test(word.trim()))?.[1] ?? null;
const STAT_RE = '(strength|str|defen[cs]e|def|speed|spd|dexterity|dex|total)';
const NUM_RE = '(n\\/a|unknown|\\?+|-?[\\d][\\d,]*(?:\\.\\d+)?\\s*[kmbtq]?)';
const MULT: Record<string, number> = { k: 1e3, m: 1e6, b: 1e9, t: 1e12, q: 1e15 };

/** "1,234,567" → 1234567; "1.61b" → 1.61e9 (approx); "N/A" → null. Undefined when it isn't a number at all. */
export function spyNumber(raw: string): { value: number | null; approx: boolean } | undefined {
  const t = raw.trim().toLowerCase();
  if (/^(n\/a|unknown|\?+)$/.test(t)) return { value: null, approx: false };
  const m = /^(-?\d[\d,]*(?:\.\d+)?)\s*([kmbtq])?$/.exec(t);
  if (!m) return undefined;
  const n = Number(m[1]!.replace(/,/g, ''));
  if (!Number.isFinite(n) || n < 0) return undefined;
  const mult = m[2] ? MULT[m[2]]! : 1;
  return { value: Math.round(n * mult), approx: !!m[2] };
}

/** A time written in the text: Torn's "HH:MM:SS - DD/MM/YY" (TCT = UTC), ISO "2026-10-29 12:34", "3 days ago". */
export function spyTime(text: string, now: number): number | null {
  const tct = /(\d{1,2}):(\d{2})(?::(\d{2}))?\s*[-–]\s*(\d{1,2})\/(\d{1,2})\/(\d{2,4})/.exec(text);
  if (tct) {
    const y = Number(tct[6]!.length === 2 ? `20${tct[6]}` : tct[6]);
    const t = Date.UTC(y, Number(tct[5]) - 1, Number(tct[4]), Number(tct[1]), Number(tct[2]), Number(tct[3] ?? 0));
    if (Number.isFinite(t)) return t;
  }
  const iso = /(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(text);
  if (iso) {
    const t = Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]), Number(iso[4] ?? 0), Number(iso[5] ?? 0), Number(iso[6] ?? 0));
    if (Number.isFinite(t)) return t;
  }
  const ago = /(\d+)\s*(minute|min|hour|hr|day|week|month)s?\s+ago/i.exec(text);
  if (ago) {
    const unit = ago[2]!.toLowerCase();
    const ms = unit.startsWith('min') ? 60_000 : unit.startsWith('h') ? 3_600_000 : unit === 'day' ? 86_400_000 : unit === 'week' ? 7 * 86_400_000 : 30 * 86_400_000;
    return now - Number(ago[1]) * ms;
  }
  return null;
}

/** A player named in a line: "Name [123]", "XID=123", "ID: 123". */
function playerIn(line: string): { id: number; name?: string } | null {
  const bracket = /([\w-]{1,30})?\s*\[(\d{1,8})\]/.exec(line);
  if (bracket) return { id: Number(bracket[2]), ...(bracket[1] && !statOf(bracket[1]) && !/^(name|player|target)$/i.test(bracket[1]) ? { name: bracket[1] } : {}) };
  const xid = /XID=(\d{1,8})/i.exec(line) ?? /\b(?:user\s*id|player\s*id|id)\s*[:=#]\s*(\d{1,8})\b/i.exec(line);
  return xid ? { id: Number(xid[1]) } : null;
}

interface Block {
  target: number | null;
  name?: string;
  level?: number;
  at: number | null;
  stats: Partial<Record<SpyStat, number | null>>;
  approx: boolean;
}

const empty = (): Block => ({ target: null, at: null, stats: {}, approx: false });
const hasStats = (b: Block) => Object.keys(b.stats).length > 0;

function finish(b: Block, now: number): ParsedSpy | null {
  const s: Record<SpyStat, number | null> = { strength: b.stats.strength ?? null, defense: b.stats.defense ?? null, speed: b.stats.speed ?? null, dexterity: b.stats.dexterity ?? null, total: b.stats.total ?? null };
  if (SPY_STATS.filter((k) => s[k] !== null).length < 2) return null;
  const done = complete(s);
  if (!done.total || done.total <= 0 || !addsUp(done)) return null;
  const out: ParsedSpy = { target: b.target, strength: done.strength, defense: done.defense, speed: done.speed, dexterity: done.dexterity, total: done.total, at: Math.min(b.at ?? now, now) };
  if (b.name) out.name = b.name;
  if (b.level) out.level = b.level;
  if (done.derived) out.derived = done.derived;
  if (b.approx) out.approx = true;
  return out;
}

/** Torn's own answer pasted as JSON: { result: { msg, user } }, or just the msg, or a list of { target/id, … }. */
function fromJson(text: string, now: number): ParsedSpy[] | null {
  const t = text.trim();
  if (!/^[[{]/.test(t)) return null;
  let x: unknown;
  try {
    x = JSON.parse(t);
  } catch {
    return null;
  }
  const list = Array.isArray(x) ? x : [x];
  const out: ParsedSpy[] = [];
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const result = (o.result && typeof o.result === 'object' ? o.result : o) as Record<string, unknown>;
    const msg = (result.msg && typeof result.msg === 'object' ? result.msg : result) as Record<string, unknown>;
    const user = (result.user && typeof result.user === 'object' ? result.user : {}) as Record<string, unknown>;
    const b = empty();
    for (const [k, v] of Object.entries(msg)) {
      const stat = statOf(k === 'defence' ? 'defense' : k);
      if (!stat) continue;
      const n = typeof v === 'number' ? { value: v, approx: false } : typeof v === 'string' ? spyNumber(v) : undefined;
      if (!n) continue;
      b.stats[stat] = n.value;
      b.approx ||= n.approx;
    }
    const id = Number(user.userID ?? o.target ?? o.id ?? o.player_id ?? 0);
    if (Number.isInteger(id) && id > 0 && id <= 1e8) b.target = id;
    const name = user.playername ?? o.name ?? o.player_name;
    if (typeof name === 'string' && /^[\w-]{1,30}$/.test(name)) b.name = name;
    const level = Number(user.level ?? o.level ?? o.player_level);
    if (Number.isInteger(level) && level >= 1 && level <= 100) b.level = level;
    const at = Number(o.at ?? o.timestamp ?? o.update ?? 0);
    if (Number.isFinite(at) && at > 0) b.at = at < 1e12 ? at * 1000 : at;
    const p = finish(b, now);
    if (p) out.push(p);
  }
  return out;
}

/** A header row naming the stats (a copied table): the column of each, plus name/id/level/date columns. */
function tableColumns(cells: string[]): { stat: Partial<Record<SpyStat, number>>; level: number; date: number; who: number } | null {
  const stat: Partial<Record<SpyStat, number>> = {};
  let level = -1;
  let date = -1;
  let who = -1;
  cells.forEach((c, i) => {
    const s = statOf(c);
    if (s && stat[s] === undefined) stat[s] = i;
    else if (/^(level|lvl|lv)$/i.test(c.trim())) level = i;
    else if (/^(date|time|when|updated|last\s*updated|spied)$/i.test(c.trim())) date = i;
    else if (/^(name|player|target|user)$/i.test(c.trim())) who = i;
  });
  return Object.keys(stat).length >= 3 ? { stat, level, date, who } : null;
}

/**
 * Every spy in pasted text, in order. Understands:
 *  - Torn's job special as copied from the page (or as other tools copy it): "Name: X [id]" / "Level: N" / "You managed to
 *    get the following results:" / "Strength: 1,234" … "Total: …" (one per line or on one line);
 *  - one line per player: "Blakey [1453358] Str: 1.61b, Def: 511m, Spd 1.27b, Dex 4.38b, Total 7.78b";
 *  - a copied table with a header row (Name, Level, Strength, Defense, Speed, Dexterity, Total, Date), tab or | separated;
 *  - Torn's own JSON answer (result.msg / result.user).
 * "N/A" stays unknown; one missing stat is worked out from the others. A time in the text is used (Torn's
 * "HH:MM:SS - DD/MM/YY", ISO dates, "3 days ago"), else `now`. A spy with no player named has target null.
 */
export function parseSpies(text: string, now: number = Date.now()): ParsedSpy[] {
  const json = fromJson(text, now);
  if (json) return json;
  const lines = text.replace(/\r/g, '').split('\n').map((l) => l.trim());
  const out: ParsedSpy[] = [];

  // A table: a header row naming at least three stats, then a row per player.
  for (let i = 0; i < lines.length; i++) {
    const sep = lines[i]!.includes('\t') ? '\t' : lines[i]!.includes('|') ? '|' : null;
    if (!sep) continue;
    const cols = tableColumns(lines[i]!.split(sep));
    if (!cols) continue;
    for (let j = i + 1; j < lines.length; j++) {
      const cells = lines[j]!.split(sep);
      if (cells.length < 3) break;
      const b = empty();
      for (const [k, idx] of Object.entries(cols.stat) as Array<[SpyStat, number]>) {
        const n = spyNumber(cells[idx] ?? '');
        if (!n) continue;
        b.stats[k] = n.value;
        b.approx ||= n.approx;
      }
      const who = playerIn(cols.who >= 0 ? (cells[cols.who] ?? '') : cells.join(' '));
      if (who) {
        b.target = who.id;
        if (who.name) b.name = who.name;
      }
      if (cols.level >= 0) {
        const lv = Number(cells[cols.level]);
        if (Number.isInteger(lv) && lv >= 1 && lv <= 100) b.level = lv;
      }
      b.at = spyTime(cols.date >= 0 ? (cells[cols.date] ?? '') : cells.join(' '), now);
      const p = finish(b, now);
      if (p) out.push(p);
    }
    return out;
  }

  // Lines: "Stat: number" pairs anywhere (several on one line too); a player named starts a new spy once the current one
  // has stats.
  const pair = new RegExp(`\\b${STAT_RE}\\b\\s*[:=-]?\\s*${NUM_RE}(?![\\w.])`, 'gi');
  let b = empty();
  const flush = () => {
    const p = hasStats(b) ? finish(b, now) : null;
    if (p) out.push(p);
    b = empty();
  };
  for (const line of lines) {
    if (!line) continue;
    const who = playerIn(line);
    if (who && hasStats(b)) flush();
    if (who && b.target === null) {
      b.target = who.id;
      if (who.name) b.name = who.name;
    } else if (b.target === null) {
      const named = /^(?:name|player|target)\s*:\s*([\w-]{1,30})\s*$/i.exec(line);
      if (named) {
        if (hasStats(b)) flush();
        b.name = named[1]!;
      }
    }
    const lv = /\b(?:level|lvl|lv)\s*[:.]?\s*(\d{1,3})\b/i.exec(line);
    if (lv && Number(lv[1]) >= 1 && Number(lv[1]) <= 100) b.level = Number(lv[1]);
    const when = spyTime(line, now);
    if (when !== null) b.at = when;
    for (const m of line.matchAll(pair)) {
      const stat = statOf(m[1]!);
      const n = spyNumber(m[2]!);
      if (!stat || !n) continue;
      // The same stat again: a new spy (a list of spies with no names between them).
      if (b.stats[stat] !== undefined) {
        const keep = { target: b.target, name: b.name, level: b.level };
        flush();
        if (keep.target === null) Object.assign(b, keep.name ? { name: keep.name } : {});
      }
      b.stats[stat] = n.value;
      b.approx ||= n.approx;
    }
  }
  flush();
  return out;
}

/** "str 1.61b · def 511m · spd 1.27b · dex 4.38b" (null: "?"). */
export function spyLine(s: Pick<Spy, 'strength' | 'defense' | 'speed' | 'dexterity'>, short: (n: number) => string): string {
  const f = (n: number | null) => (n === null ? '?' : short(n));
  return `str ${f(s.strength)} · def ${f(s.defense)} · spd ${f(s.speed)} · dex ${f(s.dexterity)}`;
}

// ---------------------------------------------------------------- "Spies & estimates" lookups (website, invite-only)

/**
 * What any invited player may see about a player in the database: what Torn shows publicly and Omni-Torn's best
 * estimate (bestEstimate: method × age; `totalSource` its heaviest method, `totalAt` its time to the hour). Never fights,
 * fight logs or who sent what.
 */
export interface EstimateFacts {
  id: number;
  name: string | null;
  level: number | null;
  factionId: number | null;
  total: number | null;
  totalSource: string | null;
  totalAt: number | null;
  lastAction: number | null;
  status: string | null;
  statusUntil: number | null;
  firstSeen: number;
}

/** GET /api/estimates/player/:id — null player: not in the database. History: oldest first, each to the day. */
export interface PlayerEstimateAnswer {
  player: EstimateFacts | null;
  history: Array<{ at: number; total: number; method: string }>;
  /** Your, your faction's and shared spies of them, newest first. */
  spies: SpyEntry[];
}

/** GET /api/estimates/faction/:id — the players the database places in that faction. */
export interface FactionEstimateAnswer {
  factionId: number;
  members: EstimateFacts[];
  /** Newest spy per member that you or your faction recorded, or that was shared with every Omni-Torn user. */
  spies: Record<string, SpyEntry>;
}

/** GET /api/estimates/find?name= — players the database knows by that name (exact first). */
export interface EstimateFindAnswer {
  players: Array<{ id: number; name: string; level: number | null }>;
}

// ---------------------------------------------------------------- good targets (worked out in the viewer's browser)

/** Battle stat score from a total with a typical build (FF Scouter's and ours: total ≈ 1.04 × score² ÷ 4). */
export const scoreOfTotal = (total: number): number => 2 * Math.sqrt(Math.max(0, total) / 1.04);

/**
 * The fair fight you'd likely get against a player with this estimated total (Torn: FF = 1 + 8/3 × their score ÷ yours,
 * 1–3). Your own total never leaves your browser.
 */
export function fairFightAgainst(theirTotal: number, yourTotal: number): number {
  const mine = scoreOfTotal(yourTotal);
  if (!(mine > 0) || !(theirTotal > 0)) return 1;
  return Math.max(1, Math.min(3, 1 + (8 / 3) * (scoreOfTotal(theirTotal) / mine)));
}

/** A good target for you: likely the full fair fight (their score ≥ ¾ of yours) while weaker than you (their total ≤ 90% of yours). */
export function goodTarget(theirTotal: number | null, yourTotal: number): boolean {
  if (theirTotal === null || !(theirTotal > 0) || !(yourTotal > 0)) return false;
  return fairFightAgainst(theirTotal, yourTotal) >= 2.9 && theirTotal <= yourTotal * 0.9;
}

/** What each estimate method means, in plain words (website, profiles). */
export const ESTIMATE_METHOD_WORDS: Record<EstimateMethod, string> = { spy: 'a spy', fight: 'Omni-Torn users’ fights', pool: 'pooled fair fights', rank: 'their rank (a rough guess)' };

/**
 * How much to believe an estimate: its method's trust × its age (the same weights bestEstimate uses), in words.
 * A fresh spy "Very high"; a fresh fight "High"; a month-old fight or a fresh pool "Fair"; a rank guess "Low".
 */
export function estimateTrustWord(method: string | null, ageMs: number): 'Very high' | 'High' | 'Fair' | 'Low' {
  const m = (method ?? 'rank') as EstimateMethod;
  const w = (ESTIMATE_METHOD_TRUST[m] ?? ESTIMATE_METHOD_TRUST.rank) * trustOf('estimate', ageMs);
  return w >= 2.5 ? 'Very high' : w >= 0.6 ? 'High' : w >= 0.25 ? 'Fair' : 'Low';
}

/**
 * A faction's members ranked as targets for you (worked out in your browser; your total is never sent): good targets
 * first (goodTarget), then by the fair fight you'd likely get, then the weaker first. Members with no estimate last.
 */
export function rankTargets<T extends { total: number | null }>(members: readonly T[], yourTotal: number): Array<T & { ff: number | null; good: boolean }> {
  return members
    .map((m) => ({ ...m, ff: m.total && m.total > 0 && yourTotal > 0 ? Math.round(fairFightAgainst(m.total, yourTotal) * 100) / 100 : null, good: goodTarget(m.total, yourTotal) }))
    .sort((a, b) => Number(b.good) - Number(a.good) || (b.ff ?? -1) - (a.ff ?? -1) || (a.total ?? Infinity) - (b.total ?? Infinity));
}
